import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  RefreshCw,
  Zap,
  Github,
  ChevronDown,
  Server,
  Cpu,
  HardDrive,
  Network,
  Boxes,
  Layers,
  Activity,
  Maximize2,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/* -------------------------------- helpers -------------------------------- */

const ok = (l: number, c: number, h: number, a = 1) =>
  `oklch(${l} ${c} ${h}${a < 1 ? ` / ${a}` : ""})`;
const C = {
  green: ok(0.78, 0.18, 150),
  yellow: ok(0.85, 0.15, 90),
  orange: ok(0.78, 0.18, 55),
  red: ok(0.7, 0.2, 30),
  blue: ok(0.72, 0.15, 240),
  purple: ok(0.72, 0.15, 290),
  cyan: ok(0.8, 0.12, 200),
  pink: ok(0.78, 0.15, 350),
  grid: ok(1, 0, 0, 0.06),
  axis: ok(0.7, 0.02, 240, 0.7),
};

function series(n: number, base: number, amp: number, seed = 1) {
  return Array.from({ length: n }, (_, i) => ({
    t: i,
    v: Math.max(0, base + Math.sin(i / 2 + seed) * amp + Math.cos(i / 4 + seed) * (amp / 2)),
  }));
}

function timeLabels(n: number) {
  const now = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(now.getTime() - (n - i) * 60_000);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
}

const TT = {
  contentStyle: {
    background: "oklch(0.18 0.01 240)",
    border: "1px solid oklch(1 0 0 / 0.08)",
    borderRadius: 8,
    fontSize: 11,
  },
  labelStyle: { color: "oklch(0.7 0.02 240)" },
};

/* -------------------------------- pill UI -------------------------------- */

function FilterPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-1.5 text-xs">
      <span className="text-[oklch(0.72_0.15_240)]">{label}</span>
      <span className="text-foreground/80">{value}</span>
      <ChevronDown className="h-3 w-3 text-muted-foreground" />
    </div>
  );
}

function Panel({
  title,
  icon: Icon,
  children,
  className = "",
}: {
  title: string;
  icon?: typeof Server;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-lg border border-white/10 bg-[oklch(0.16_0.01_240)] p-3 ${className}`}>
      <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          <span>{title}</span>
        </div>
        <Maximize2 className="h-3 w-3 opacity-50" />
      </div>
      {children}
    </div>
  );
}

function RatioBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="text-sm font-medium" style={{ color }}>
        {pct.toFixed(1)}%
      </div>
      <div className="relative h-12 w-12 overflow-hidden rounded">
        <div className="absolute inset-x-0 bottom-0 bg-white/5" style={{ height: "100%" }} />
        <div
          className="absolute inset-x-0 bottom-0"
          style={{ height: `${pct}%`, background: color, opacity: 0.55 }}
        />
      </div>
      <div className="truncate text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function BigStat({ value, label, color }: { value: string; label: string; color: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="text-xl font-medium" style={{ color }}>
        {value}
      </div>
      <div className="h-12 w-8 rounded" style={{ background: color, opacity: 0.55 }} />
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

/* ------------------------------ summary banner --------------------------- */

function SummaryStrip() {
  const items = [
    { v: "69", l: "Workload", c: "oklch(0.55 0.18 145)" },
    { v: "209", l: "Total Pod", c: "oklch(0.7 0.16 90)" },
    { v: "10", l: "Total Nodes", c: "oklch(0.6 0.18 250)" },
    { v: "3", l: "Normal Node", c: "oklch(0.65 0.18 55)" },
    { v: "4", l: "purpose", c: "oklch(0.6 0.2 25)" },
    { v: "3", l: "node-role.kubernetes.io/control-plane", c: "oklch(0.6 0.18 250)" },
  ];
  return (
    <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-6">
      {items.map((i) => (
        <div
          key={i.l}
          className="flex items-center justify-between rounded px-3 py-3"
          style={{ background: i.c }}
        >
          <span className="truncate text-[11px] text-white/90">{i.l}</span>
          <span className="text-2xl font-medium text-white">{i.v}</span>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------- view ---------------------------------- */

export default function KubernetesMetricsView({ onBack }: { onBack: () => void }) {
  const [tick, setTick] = useState(0);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [auto]);

  const N = 30;
  const labels = useMemo(() => timeLabels(N), [tick]);

  const memUsage = useMemo(
    () => series(N, 235, 4, tick).map((d, i) => ({ ...d, label: labels[i], limit: 256 })),
    [tick, labels],
  );
  const cpuCores = useMemo(
    () => series(N, 82, 3, tick + 1).map((d, i) => ({ ...d, label: labels[i], limit: 100 })),
    [tick, labels],
  );
  const podCount = useMemo(
    () =>
      series(N, 209, 2, tick + 2).map((d, i) => ({
        label: labels[i],
        pods: Math.round(d.v),
        nodes: 10,
        limit: 1000,
      })),
    [tick, labels],
  );

  const nodeCpuBreakdown = useMemo(() => {
    const nodes = ["node-01", "node-02", "node-03", "node-04", "node-05"];
    return Array.from({ length: N }, (_, i) => {
      const row: Record<string, number | string> = { label: labels[i] };
      nodes.forEach((n, k) => {
        row[n] = 25 + Math.sin(i / 2 + k + tick) * 12 + k * 2;
      });
      return row;
    });
  }, [tick, labels]);

  const nodeMemBreakdown = useMemo(() => {
    const nodes = ["node-01", "node-02", "node-03", "node-04", "node-05"];
    return Array.from({ length: N }, (_, i) => {
      const row: Record<string, number | string> = { label: labels[i] };
      nodes.forEach((n, k) => {
        row[n] = 8 + Math.sin(i / 3 + k) * 3 + k * 2.5;
      });
      return row;
    });
  }, [labels]);

  const nodeNet = useMemo(
    () =>
      Array.from({ length: N }, (_, i) => ({
        label: labels[i],
        inflow: 30 + Math.sin(i / 2 + tick) * 18,
        outflow: -(20 + Math.cos(i / 2 + tick) * 14),
      })),
    [tick, labels],
  );

  const microNet = useMemo(() => {
    const svcs = ["cilium-agent", "kube-proxy", "node-exporter", "liveness-probe", "nginx-proxy"];
    return Array.from({ length: 40 }, (_, i) => {
      const row: Record<string, number | string> = { label: i };
      svcs.forEach((s, k) => {
        row[s] = 8 + Math.sin(i / 2 + k + tick) * 6 + k;
      });
      return row;
    });
  }, [tick]);

  const microCpu = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        label: i,
        clickhouse: 16 + Math.sin(i / 3) * 1,
        main: 5 + Math.cos(i / 4) * 0.5,
        cilium: 1 + Math.sin(i / 5) * 0.3,
      })),
    [],
  );

  const microMem = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        label: i,
        cilium: 2.4 + Math.sin(i / 4) * 0.05,
        apiserver: 1.85 + Math.cos(i / 5) * 0.03,
        prometheus: 1.1 + Math.sin(i / 6) * 0.04,
      })),
    [],
  );

  const namespaces = [
    { name: "default", micro: 18, pods: 24 },
    { name: "argo", micro: 11, pods: 14 },
    { name: "kube-system", micro: 10, pods: 52 },
    { name: "harbor", micro: 7, pods: 9 },
    { name: "monitoring", micro: 6, pods: 18 },
    { name: "cert-manager", micro: 4, pods: 6 },
  ];

  const nodeInfo = [
    { node: "node-01", cpuReq: 48, memReq: 28, cpuLim: 106, memLim: 39, cpu: 1.65, cpuL: 3.6, mem: "4.35 GiB", memL: "5.72 GiB", container: "kube-state-metrics", ep: "http", ns: "monitoring" },
    { node: "node-02", cpuReq: 68, memReq: 55, cpuLim: 18, memLim: 7, cpu: 2.3, cpuL: 0.625, mem: "8.68 GiB", memL: "989 MiB", container: "kube-state-metrics", ep: "http", ns: "monitoring" },
    { node: "node-03", cpuReq: 7, memReq: 1, cpuLim: 4, memLim: 7, cpu: 0.75, cpuL: 0.5, mem: "1.37 GiB", memL: "777 MiB", container: "kube-state-metrics", ep: "http", ns: "monitoring" },
    { node: "node-04", cpuReq: 7, memReq: 1, cpuLim: 4, memLim: 7, cpu: 0.75, cpuL: 0.5, mem: "1.37 GiB", memL: "777 MiB", container: "kube-state-metrics", ep: "http", ns: "monitoring" },
    { node: "node-05", cpuReq: 6, memReq: 1, cpuLim: 4, memLim: 4, cpu: 0.67, cpuL: 0.5, mem: "744 MiB", memL: "477 MiB", container: "kube-state-metrics", ep: "http", ns: "monitoring" },
  ];

  const heat = (v: number) => {
    if (v >= 100) return C.red;
    if (v >= 60) return C.orange;
    if (v >= 30) return C.yellow;
    return C.green;
  };

  return (
    <div className="space-y-3">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/5 hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="text-lg font-medium">K8S Cluster</div>
            <div className="text-xs text-muted-foreground">
              Nodes · Namespaces · Microservices · Pods — live
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FilterPill label="K8S" value="None" />
          <FilterPill label="Nodes" value="All" />
          <FilterPill label="Namespace" value="All" />
          <FilterPill label="Microservice" value="All" />
          <FilterPill label="Pod" value="All" />
          <button
            onClick={() => setAuto((a) => !a)}
            className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
              auto
                ? "border-[oklch(0.7_0.18_140/0.4)] bg-[oklch(0.7_0.18_140/0.1)] text-[oklch(0.78_0.18_150)]"
                : "border-white/10 bg-white/[0.02] text-muted-foreground"
            }`}
          >
            <Zap className="h-3 w-3" /> Update
          </button>
          <button
            onClick={() => setTick((t) => t + 1)}
            className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5 text-xs"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
          <button className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5 text-xs">
            <Github className="h-3 w-3" /> GitHub
          </button>
        </div>
      </div>

      {/* Node Resource Overview header */}
      <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
        <ChevronDown className="h-3.5 w-3.5" />
        <span>Node Resource Overview: Selected Nodes:</span>
        <span className="text-foreground">【All】</span>
      </div>

      {/* Row 1 — KPI panels */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
        <Panel title="Node Memory Ratio" icon={HardDrive}>
          <div className="grid grid-cols-3 gap-2">
            <RatioBar label="Mem Used %" pct={4.9} color={C.green} />
            <RatioBar label="Mem Req %" pct={40.5} color={C.green} />
            <RatioBar label="Mem Lim %" pct={36.9} color={C.green} />
          </div>
        </Panel>
        <Panel title="Node CPU Ratio" icon={Cpu}>
          <div className="grid grid-cols-3 gap-2">
            <RatioBar label="CPU Used %" pct={2.4} color={C.green} />
            <RatioBar label="CPU Req %" pct={36.3} color={C.green} />
            <RatioBar label="CPU Lim %" pct={39.8} color={C.green} />
          </div>
        </Panel>
        <Panel title="Nodes / Pods / Updates" icon={Server}>
          <div className="grid grid-cols-3 gap-2">
            <BigStat value="10" label="Nodes" color={C.green} />
            <BigStat value="203" label="Pods" color={C.green} />
            <BigStat value="1100" label="Upper" color={C.orange} />
          </div>
        </Panel>
        <Panel title="Namespace Resource Stats" icon={Layers}>
          <div className="grid grid-cols-3 gap-x-2 text-[11px]">
            <div className="text-muted-foreground">Spaces</div>
            <div className="text-right text-muted-foreground">Micro</div>
            <div className="text-right text-muted-foreground">Pods</div>
            {namespaces.map((n) => (
              <div key={n.name} className="contents">
                <div className="truncate py-0.5" style={{ color: C.green }}>
                  {n.name}
                </div>
                <div className="py-0.5 text-right" style={{ color: C.yellow }}>
                  {n.micro}
                </div>
                <div className="py-0.5 text-right" style={{ color: C.blue }}>
                  {n.pods}
                </div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="All: Network Overview" icon={Network}>
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={nodeNet} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="ovrIn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.green} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={C.green} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} tickFormatter={(v) => `${Math.abs(v)} Mib/s`} />
              <Tooltip {...TT} />
              <Area type="monotone" dataKey="inflow" stroke={C.green} fill="url(#ovrIn)" />
              <Area type="monotone" dataKey="outflow" stroke={C.yellow} fillOpacity={0} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>Receive Max: 249 Mib/s</span>
            <span>Send Max: 142 Mib/s</span>
          </div>
        </Panel>
      </div>

      {/* Summary strip */}
      <SummaryStrip />

      {/* Row — Memory / CPU / Pod count */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Panel title="Memory Usage【All】" icon={HardDrive}>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={memUsage} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="memUse" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.red} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={C.red} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} tickFormatter={(v) => `${v} GiB`} domain={[0, 280]} />
              <Tooltip {...TT} />
              <Area type="monotone" dataKey="v" stroke={C.red} fill="url(#memUse)" name="Memory" />
              <Line type="monotone" dataKey="limit" stroke={C.yellow} dot={false} strokeWidth={1} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="CPU Used Cores【All】" icon={Cpu}>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={cpuCores} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="cpuUse" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.red} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={C.red} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} domain={[0, 180]} />
              <Tooltip {...TT} />
              <Area type="monotone" dataKey="v" stroke={C.red} fill="url(#cpuUse)" name="Cores" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Pod Number and nodes【All】" icon={Boxes}>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={podCount} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis yAxisId="l" tick={{ fontSize: 9, fill: C.axis }} domain={[0, 1100]} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9, fill: C.axis }} domain={[0, 20]} />
              <Tooltip {...TT} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line yAxisId="l" type="monotone" dataKey="pods" stroke={C.yellow} name="Pod Number" dot={false} />
              <Line yAxisId="l" type="monotone" dataKey="limit" stroke={C.red} name="Upper Limit Pod" dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="nodes" stroke={C.blue} name="Number of nodes" dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Row — Node breakdowns */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Panel title="All: Nodes CPU Breakdown" icon={Cpu}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={nodeCpuBreakdown} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} tickFormatter={(v) => `${v}%`} />
              <Tooltip {...TT} />
              {["node-01", "node-02", "node-03", "node-04", "node-05"].map((n, i) => (
                <Line
                  key={n}
                  type="monotone"
                  dataKey={n}
                  stroke={[C.yellow, C.green, C.orange, C.purple, C.cyan][i]}
                  dot={false}
                  strokeWidth={1.2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="All: Node Memory Breakdown" icon={HardDrive}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={nodeMemBreakdown} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} tickFormatter={(v) => `${v}%`} />
              <Tooltip {...TT} />
              {["node-01", "node-02", "node-03", "node-04", "node-05"].map((n, i) => (
                <Line
                  key={n}
                  type="monotone"
                  dataKey={n}
                  stroke={[C.yellow, C.green, C.pink, C.purple, C.blue][i]}
                  dot={false}
                  strokeWidth={1.2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="All: Node Network Overview" icon={Network}>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={nodeNet} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="netIn2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.green} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={C.green} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} tickFormatter={(v) => `${Math.abs(v)} Mib/s`} />
              <Tooltip {...TT} />
              <Area type="monotone" dataKey="inflow" stroke={C.green} fill="url(#netIn2)" />
              <Area type="monotone" dataKey="outflow" stroke={C.orange} fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Node Information Detail */}
      <Panel title="All: Node Information Detail" icon={Server}>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="text-muted-foreground">
              <tr className="border-b border-white/5">
                {["CPU Requ", "Memory R", "CPU Limit", "Memory L", "CPU Req", "CPU Limit", "Memory", "Memory Limit", "container", "endpoint", "namespace"].map((h) => (
                  <th key={h} className="px-2 py-2 text-left font-normal">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {nodeInfo.map((r, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="px-2 py-1.5">
                    <span className="rounded px-2 py-0.5 font-medium text-black" style={{ background: heat(r.cpuReq) }}>
                      {r.cpuReq}%
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="rounded px-2 py-0.5 font-medium text-black" style={{ background: heat(r.memReq) }}>
                      {r.memReq}%
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="rounded px-2 py-0.5 font-medium text-black" style={{ background: heat(r.cpuLim) }}>
                      {r.cpuLim}%
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="rounded px-2 py-0.5 font-medium text-black" style={{ background: heat(r.memLim) }}>
                      {r.memLim}%
                    </span>
                  </td>
                  <td className="px-2 py-1.5 tabular-nums">{r.cpu}</td>
                  <td className="px-2 py-1.5 tabular-nums">{r.cpuL}</td>
                  <td className="px-2 py-1.5">{r.mem}</td>
                  <td className="px-2 py-1.5">{r.memL}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{r.container}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{r.ep}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{r.ns}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Microservices Network bandwidth */}
      <Panel title="Microservice (Container Name) Network bandwidth per second (Associable Nodes)" icon={Activity}>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={microNet} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
            <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
            <YAxis tick={{ fontSize: 9, fill: C.axis }} tickFormatter={(v) => `${v} Mib/s`} />
            <Tooltip {...TT} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {["cilium-agent", "kube-proxy", "node-exporter", "liveness-probe", "nginx-proxy"].map((s, i) => (
              <Line
                key={s}
                type="monotone"
                dataKey={s}
                stroke={[C.green, C.yellow, C.purple, C.blue, C.red][i]}
                dot={false}
                strokeWidth={1.2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      {/* Microservices CPU + Memory */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel title="Microservices (Container Name) Overall CPU Cores" icon={Cpu}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={microCpu} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} />
              <Tooltip {...TT} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="clickhouse" stroke={C.red} dot={false} name="clickhouse-server" />
              <Line type="monotone" dataKey="main" stroke={C.orange} dot={false} name="main" />
              <Line type="monotone" dataKey="cilium" stroke={C.green} dot={false} name="cilium-agent" />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Microservices (Container Name) Overall Memory" icon={HardDrive}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={microMem} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.axis }} />
              <YAxis tick={{ fontSize: 9, fill: C.axis }} tickFormatter={(v) => `${v} GiB`} />
              <Tooltip {...TT} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="cilium" stroke={C.green} dot={false} name="WSS: cilium-agent" />
              <Line type="monotone" dataKey="apiserver" stroke={C.pink} dot={false} name="WSS: kube-apiserver" />
              <Line type="monotone" dataKey="prometheus" stroke={C.purple} dot={false} name="RSS: prometheus" />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}
